import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../context/AuthContext';

const Container: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <div style={{ maxWidth: 760, margin: '24px auto', padding: 16 }}>{children}</div>
);
const Card: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 20, boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>{children}</div>
);
const Row: React.FC<{ children?: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
  <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12, ...style }}>{children}</div>
);
const Label: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <div style={{ width: 180, fontWeight: 600 }}>{children}</div>
);
const Input: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = (props) => (
  <input {...props} style={{ flex: 1, padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc' }} />
);
const Textarea: React.FC<React.TextareaHTMLAttributes<HTMLTextAreaElement>> = (props) => (
  <textarea {...props} style={{ flex: 1, padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc', minHeight: 120, fontFamily: 'monospace' }} />
);
const Button: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({ children, ...rest }) => (
  <button {...rest} style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 14px', fontWeight: 600, cursor: 'pointer' }}>{children}</button>
);
const Muted: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <div style={{ fontSize: 12, color: '#666' }}>{children}</div>
);

interface AdsenseCfg {
  enabled: boolean;
  publisher_id?: string | null;
  auto_ads?: boolean;
  slot_map?: Record<string, string>;
  head_snippet?: string | null;
}

const sampleMapping = `{
  "ad-bill-*": "1234567890",
  "ad-tag-*": "0987654321",
  "sidebar-top": "1122334455"
}`;

const AdsenseAdmin: React.FC = () => {
  const navigate = useNavigate();
  const { keycloak } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [enabled, setEnabled] = useState<boolean>(false);
  const [publisherId, setPublisherId] = useState<string>('');
  const [autoAds, setAutoAds] = useState<boolean>(false);
  const [slotMapText, setSlotMapText] = useState<string>('{}');
  const [headSnippet, setHeadSnippet] = useState<string>('');

  const isAdmin = useMemo(() => {
    try {
      const tokenParsed: any = keycloak?.tokenParsed || {};
      const groups: string[] = tokenParsed?.groups || tokenParsed?.realm_access?.groups || [] as any;
      return Array.isArray(groups) && groups.some(g => String(g).replace(/^\//, '') === 'administrator');
    } catch {
      return false;
    }
  }, [keycloak]);

  useEffect(() => {
    if (!isAdmin) { setLoading(false); return; }
    (async () => {
      try {
        if (keycloak?.token) apiService.setToken(keycloak.token);
        const resp = await apiService.getAdminAdsenseConfig();
        const cfg: AdsenseCfg = resp.data || {};
        setEnabled(!!cfg.enabled);
        setPublisherId(cfg.publisher_id || '');
        setAutoAds(!!cfg.auto_ads);
        setSlotMapText(JSON.stringify(cfg.slot_map || {}, null, 2));
        setHeadSnippet(cfg.head_snippet || '');
      } catch (e: any) {
        setError(e?.response?.data?.detail || e?.message || 'Failed to load AdSense config');
      } finally {
        setLoading(false);
      }
    })();
  }, [isAdmin, keycloak]);

  const handleSave = async () => {
    setError(null); setSuccess(null);
    let mapObj: Record<string, string> = {};
    try {
      mapObj = JSON.parse(slotMapText || '{}');
      if (typeof mapObj !== 'object' || Array.isArray(mapObj)) throw new Error('slot_map must be an object of { slotIdOrPattern: adUnitId }');
    } catch (e: any) {
      setError('Invalid JSON for Slot Mapping: ' + (e?.message || 'parse error'));
      return;
    }
    if (enabled && (!publisherId || !publisherId.startsWith('ca-pub-'))) {
      setError("publisher_id must start with 'ca-pub-' when enabled");
      return;
    }
    try {
      setSaving(true);
      const payload = { enabled, publisher_id: publisherId || null, auto_ads: autoAds, slot_map: mapObj, head_snippet: headSnippet || null };
      const resp = await apiService.updateAdminAdsenseConfig(payload);
      setSuccess('Saved');
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to save config');
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <Container>
        <Card>
          <h2>AdSense Configuration</h2>
          <p>You must be an administrator to access this page.</p>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card>
        <h2 style={{ marginTop: 0 }}>AdSense Configuration</h2>
        {loading ? (
          <div>Loading…</div>
        ) : (
          <>
            {error && <div style={{ color: '#b91c1c', marginBottom: 12 }}>{error}</div>}
            {success && <div style={{ color: '#065f46', marginBottom: 12 }}>{success}</div>}

            <Row>
              <Label>Enable AdSense</Label>
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            </Row>
            <Muted>When enabled, pages with AdSlot components can be filled using your AdSense settings below.</Muted>

            <Row>
              <Label>Publisher ID</Label>
              <Input placeholder="ca-pub-xxxxxxxxxxxxxxxx" value={publisherId} onChange={(e) => setPublisherId(e.target.value)} />
            </Row>
            <Muted>Format: ca-pub-XXXXXXXXXXXXXXXX. Required when enabled.</Muted>

            <Row>
              <Label>Auto Ads</Label>
              <input type="checkbox" checked={autoAds} onChange={(e) => setAutoAds(e.target.checked)} />
            </Row>
            <Muted>If enabled, Google may place ads automatically. You can still map specific slots below.</Muted>

            <Row style={{ alignItems: 'flex-start' }}>
              <Label>Slot Mapping (JSON)</Label>
              <Textarea value={slotMapText} onChange={(e) => setSlotMapText(e.target.value)} placeholder={sampleMapping} />
            </Row>
            <Muted>Map AdSlot ids or wildcard patterns (e.g., ad-bill-*) to AdSense ad unit ids. Example shown above.</Muted>

            <Row style={{ alignItems: 'flex-start' }}>
              <Label>Head Snippet (optional)</Label>
              <Textarea value={headSnippet} onChange={(e) => setHeadSnippet(e.target.value)} placeholder={`<script async src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-XXXX" crossorigin="anonymous"></script>`} />
            </Row>
            <Muted>Paste the exact snippet AdSense gives you for site ownership verification. We will inject it into the &lt;head&gt; on every page. You can leave this empty if you prefer automatic script injection via Publisher ID.</Muted>

            <Row>
              <div style={{ width: 180 }} />
              <Button disabled={saving} onClick={handleSave}>{saving ? 'Saving…' : 'Save Settings'}</Button>
              <button onClick={() => navigate(-1)} style={{ marginLeft: 8, background: 'transparent', border: '1px solid #ccc', borderRadius: 6, padding: '10px 14px', cursor: 'pointer' }}>Back</button>
            </Row>
          </>
        )}
      </Card>
    </Container>
  );
};

export default AdsenseAdmin;
